import { Listing } from '@/components/JobCard';
import { TRADES, Lang } from '@/data/constants';

/**
 * FRESHNESS / LIVE-DATA CONTRACT: see src/lib/homeStatsFreshness.ts.
 * Aggregated server-side, cached 60s per edge isolate, no browser cache, so a
 * published / closed / expired / sold-out project reaches the home page within
 * 60 seconds with NO deployment or code change. The 2026-09-23 fixture test
 * (create -> publish -> sell out -> delete) is recorded there and rendered in
 * Admin -> Settings -> "Home page statistics".
 */

/**
 * HOME PAGE MARKETPLACE STATS — derived from real rows, never invented.
 *
 * ------------------------------------------------------------------------
 * WHY THIS FILE IS SO CONSERVATIVE
 * ------------------------------------------------------------------------
 * A construction lead marketplace is exactly the category that reputation
 * vendors (AlphaMountain, Talos, Fortinet, Sophos) and enterprise IT reviewers
 * scrutinise for fabricated social proof. Hardcoded counters like
 * "1,200+ contractors", "6 contractors viewing now" or "$2.4M+ project value"
 * are the classic lead-gen / low-trust fingerprint: they are unverifiable, they
 * never change between visits, and a single screenshot comparison exposes them.
 * Google's spam policy treats invented aggregate figures as deceptive, and a
 * school district's procurement review will ask for substantiation.
 *
 * So every number this module returns is computed from listing rows that are
 * actually in the database, and anything that cannot be computed is returned as
 * null so the UI omits the tile entirely rather than filling it with fiction.
 *
 * Specifically NOT provided, because the public feed cannot substantiate them:
 *   - registered contractor count   (requires a profile aggregate, not a feed)
 *   - "N people viewing right now"  (requires real presence tracking)
 *   - star ratings / review counts  (no review system exists yet)
 * When those systems exist, add them here — with a real source — and the tiles
 * will appear on their own.
 */

/** Midpoint of each posted budget band, in dollars. */
const BUDGET_MIDPOINTS: { test: RegExp; mid: number }[] = [
  { test: /under\s*\$?5,?000|menos de \$?5,?000/i, mid: 3500 },
  { test: /\$?5,?000\s*-\s*\$?15,?000/i, mid: 10000 },
  { test: /\$?15,?000\s*-\s*\$?30,?000/i, mid: 22500 },
  { test: /\$?30,?000\s*-\s*\$?60,?000/i, mid: 45000 },
  { test: /\$?60,?000\s*-\s*\$?100,?000/i, mid: 80000 },
  { test: /\$?100,?000\s*\+|más de \$?100,?000/i, mid: 125000 },
];

/**
 * Conservative dollar value of a posted budget band. Uses the MIDPOINT, and the
 * open-ended "$100,000+" band is floored at 125k rather than extrapolated, so
 * the headline figure understates rather than overstates available work.
 */
export const budgetMidpoint = (range?: string | null): number => {
  if (!range) return 0;
  const hit = BUDGET_MIDPOINTS.find((b) => b.test.test(range));
  return hit ? hit.mid : 0;
};

export interface MarketplaceStats {
  /** Open projects visible on the public feed right now. */
  activeProjects: number;
  /** Distinct states with at least one open project. */
  statesCovered: number;
  /** Trade categories the platform accepts work in. */
  tradesCovered: number;
  /** Distinct trade categories that currently have at least one open project. */
  tradesWithOpenWork: number;
  /**
   * Summed midpoint of posted budget ranges, in whole dollars. This is the
   * value HOMEOWNERS AND GCs SAID they budgeted — not revenue, not booked work.
   * Always render it with the `home.statValueNote` disclaimer.
   */
  postedBudgetValue: number;
  /** How many of the counted projects actually carried a budget range. */
  budgetSampleSize: number;
  /** Projects a homeowner posted (vs. GC work orders). */
  homeownerProjects: number;
  /** Projects a general contractor posted as a work order. */
  gcWorkOrders: number;
}

/**
 * ------------------------------------------------------------------------
 * NO LEAD-AGE SIGNALS, DIRECT OR INDIRECT
 * ------------------------------------------------------------------------
 * This module deliberately exposes NOTHING derived from `created_at`.
 * Counters like "new in the last 24 hours" or "posted this week" let a
 * contractor infer how fresh (or how stale) the inventory is, and that changes
 * how they value a lead for reasons that have nothing to do with the work
 * itself. Contractors should evaluate an opportunity on trade, scope, budget
 * and location — not on a timestamp.
 *
 * Do not reintroduce a recency counter here. Lead age is still available to
 * ADMIN reporting and internal analytics, which query `created_at` directly
 * against the database rather than going through these public home page stats.
 */
export const EMPTY_STATS: MarketplaceStats = {
  activeProjects: 0,
  statesCovered: 0,
  tradesCovered: TRADES.length,
  tradesWithOpenWork: 0,
  postedBudgetValue: 0,
  budgetSampleSize: 0,
  homeownerProjects: 0,
  gcWorkOrders: 0,
};
/**
 * Compute every home page stat from the listing rows we already fetched.
 *
 * ⚠️ PAGE-SCOPED — USE ONLY AS A FALLBACK.
 * This counts the ROWS IT IS GIVEN, and the browse feed hands out at most
 * MAX_PAGE_SIZE (60) rows per request. That is exactly how the home page came
 * to claim "60 Active Projects / 12 States" while 143 projects were open
 * across 17 states: the counters described one page, not the marketplace.
 *
 * `statsFromFacets()` below is the accurate path. deriveStats() is kept for
 * the case where the aggregate request fails, so the hero degrades to
 * "at least this many" rather than to nothing.
 */
export const deriveStats = (listings: Listing[]): MarketplaceStats => {
  if (!listings.length) return EMPTY_STATS;

  const states = new Set<string>();
  const trades = new Set<string>();
  let postedBudgetValue = 0;
  let budgetSampleSize = 0;
  let homeownerProjects = 0;
  let gcWorkOrders = 0;

  for (const l of listings) {
    if (l.state) states.add(l.state.toUpperCase());
    if (l.trade) trades.add(String(l.trade).toLowerCase());

    const mid = budgetMidpoint(l.budget_range);
    if (mid > 0) {
      postedBudgetValue += mid;
      budgetSampleSize++;
    }

    if (l.source_type === 'gc') gcWorkOrders++;
    else homeownerProjects++;
  }

  return {
    activeProjects: listings.length,
    statesCovered: states.size,
    tradesCovered: TRADES.length,
    tradesWithOpenWork: trades.size,
    postedBudgetValue,
    budgetSampleSize,
    homeownerProjects,
    gcWorkOrders,
  };
};

/**
 * ---------------------------------------------------------------------------
 * AGGREGATE FACETS — the WHOLE open marketplace, counted server-side.
 * ---------------------------------------------------------------------------
 * Returned by `browse-listings` in `{ stats: true }` mode. It contains counts
 * only — no ids, slugs, titles or cities — so it cannot be used to enumerate
 * listings, and it is subject to the same per-IP rate limit as a browse call.
 *
 * `budgetBands` is a histogram (posted band -> how many open projects), NOT a
 * dollar figure. The band -> midpoint table lives HERE and nowhere else, so
 * there is exactly one definition of what a posted budget is worth; the server
 * never second-guesses it.
 */
export interface MarketplaceFacets {
  activeProjects: number;
  statesCovered: number;
  tradesWithOpenWork: number;
  projectTypesCovered?: number;
  homeownerProjects: number;
  gcWorkOrders: number;
  budgetBands: Record<string, number>;
  generatedAt?: string;
  cached?: boolean;
}

/**
 * Turn server-side facets into the stat block the hero renders.
 *
 * Counting rules (business intent, stated explicitly so they are auditable):
 *  - ACTIVE PROJECTS  = published AND listing_open (open capacity, not stale).
 *    Drafts, archived, sold-out and expired projects are excluded, because a
 *    contractor cannot buy them.
 *  - STATES           = distinct state on those same rows.
 *  - TRADES COVERED   = TRADES.length — the trade catalogue the platform
 *    accepts work in. This is a capability, not an inventory count, and it is
 *    deliberately NOT the same number as tradesWithOpenWork (which is how many
 *    trades have a live project right now).
 *  - POSTED BUDGET    = sum of band midpoints across every open project that
 *    posted a budget. Projects with no budget on file contribute $0 and are
 *    excluded from budgetSampleSize, so the disclaimer stays truthful.
 *  - HOMEOWNER vs GC  = source_type, counted across the whole feed.
 */
export const statsFromFacets = (f: MarketplaceFacets): MarketplaceStats => {
  let postedBudgetValue = 0;
  let budgetSampleSize = 0;

  for (const [band, countRaw] of Object.entries(f.budgetBands || {})) {
    const count = Number(countRaw) || 0;
    if (count <= 0) continue;
    const mid = budgetMidpoint(band);
    if (mid > 0) {
      postedBudgetValue += mid * count;
      budgetSampleSize += count;
    }
  }

  return {
    activeProjects: Number(f.activeProjects) || 0,
    statesCovered: Number(f.statesCovered) || 0,
    tradesCovered: TRADES.length,
    tradesWithOpenWork: Number(f.tradesWithOpenWork) || 0,
    postedBudgetValue,
    budgetSampleSize,
    homeownerProjects: Number(f.homeownerProjects) || 0,
    gcWorkOrders: Number(f.gcWorkOrders) || 0,
  };
};



/**
 * Compact money label for big figures: 125000 -> "$125K", 2400000 -> "$2.4M".
 * Returns null for 0 so the caller can omit the tile instead of showing "$0".
 */
export const compactMoney = (dollars: number): string | null => {
  if (!dollars || dollars <= 0) return null;
  if (dollars >= 1_000_000) {
    const m = dollars / 1_000_000;
    return `$${m >= 10 ? Math.round(m) : m.toFixed(1)}M`;
  }
  if (dollars >= 1000) return `$${Math.round(dollars / 1000)}K`;
  return `$${dollars}`;
};

/** Plain-language note explaining what postedBudgetValue actually measures. */
export const budgetValueNote = (stats: MarketplaceStats, lang: Lang): string =>
  lang === 'es'
    ? `Suma de los rangos de presupuesto publicados en ${stats.budgetSampleSize} proyectos abiertos. No es ingreso ni trabajo contratado.`
    : `Sum of budget ranges posted across ${stats.budgetSampleSize} open projects. Not revenue and not booked work.`;
